import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiSave, FiArrowLeft, FiLoader, FiSend, FiTarget, FiFlag } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import theme from '../styles/theme';
import Layout from '../components/Layout';
import { mensagensEtapaAPI, funisProspeccaoAPI, etapasAPI, origensLeadAPI } from '../services/api';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const BackButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Form = styled.form`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Required = styled.span`
  color: ${theme.colors.error[500]};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 160px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  option {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: flex-end;
  padding-top: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => props.$primary ? theme.colors.primary[500] : theme.colors.border.primary};
  background: ${props => props.$primary ? theme.colors.primary[500] : 'transparent'};
  color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  min-width: 120px;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.background.primary};
    color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.primary[500]};
    border-color: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.error[600]};
  font-size: ${theme.fontSizes.sm};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.success[600]};
  font-size: ${theme.fontSizes.sm};
`;

const MensagemEtapaForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = id && id !== 'novo';
  
  // Ref para evitar chamadas duplicadas
  const hasLoadedRef = React.useRef(false);

  const [formData, setFormData] = useState({
    funil_id: '',
    etapa_id: '',
    canal: 'whatsapp',
    mensagem: '',
    ativa: true
  });

  const [funis, setFunis] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [origens, setOrigens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [loadingEtapas, setLoadingEtapas] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const carregarFunis = async () => {
    try {
      const response = await funisProspeccaoAPI.listarTodos();
      if (response.data.success) {
        setFunis(response.data.data || []);
        return response.data.data || [];
      }
      return [];
    } catch (err) {
      console.error('Erro ao carregar funis:', err);
      return [];
    }
  };

  const carregarOrigens = async () => {
    try {
      const response = await origensLeadAPI.listar();
      if (response.data.success) {
        const origensData = response.data.data || [];
        setOrigens(origensData);
        return origensData;
      }
      return [];
    } catch (err) {
      console.error('Erro ao carregar origens de lead:', err);
      return [];
    }
  };

  const carregarEtapasDoFunil = async (funilId, etapaIdParaSelecionar = null) => {
    if (!funilId) {
      setEtapas([]);
      setFormData((prev) => ({ ...prev, etapa_id: '' }));
      return;
    }

    try {
      setLoadingEtapas(true);
      const response = await etapasAPI.listarPorFunil(funilId);
      if (response.data.success) {
        const lista = response.data.data || response.data || [];
        setEtapas(lista);
        
        // Se temos uma etapa para selecionar, garantir que ela está selecionada
        if (etapaIdParaSelecionar) {
          const etapaExiste = lista.find(e => e.id === etapaIdParaSelecionar);
          if (etapaExiste) {
            // Usar callback para garantir que estamos atualizando o estado correto
            setFormData((prev) => {
              // Só atualizar se o funil_id ainda for o mesmo
              if (prev.funil_id === funilId) {
                return { ...prev, etapa_id: etapaIdParaSelecionar };
              }
              return prev;
            });
          } else if (lista.length > 0) {
            // Se a etapa não existe mais, selecionar a primeira
            setFormData((prev) => {
              if (prev.funil_id === funilId) {
                return { ...prev, etapa_id: lista[0].id };
              }
              return prev;
            });
          }
        } else {
          // Se não há etapa para selecionar e a lista não está vazia, manter o valor atual ou selecionar a primeira
          setFormData((prev) => {
            if (prev.funil_id === funilId) {
              // Se já tem uma etapa_id definida, verificar se ela existe na lista
              if (prev.etapa_id) {
                const etapaAtualExiste = lista.find(e => e.id === prev.etapa_id);
                if (etapaAtualExiste) {
                  return prev; // Manter a etapa atual se ela existe
                }
              }
              // Se não tem etapa ou a etapa atual não existe, selecionar a primeira
              if (lista.length > 0) {
                return { ...prev, etapa_id: lista[0].id };
              }
            }
            return prev;
          });
        }
      }
    } catch (err) {
      console.error('Erro ao carregar etapas:', err);
      setEtapas([]);
    } finally {
      setLoadingEtapas(false);
    }
  };

  const carregarMensagem = async () => {
    try {
      setLoadingData(true);
      setError(null);
      
      // Aguardar funis e origens carregarem primeiro e pegar os dados retornados
      const [funisData, origensData] = await Promise.all([
        carregarFunis(), 
        carregarOrigens()
      ]);
      
      const response = await mensagensEtapaAPI.buscarPorId(id);
      
      console.log('[MensagemEtapaForm] Resposta buscarPorId:', response.data);
      console.log('[MensagemEtapaForm] Funis carregados:', funisData);

      if (response.data.success) {
        const mensagemData = response.data.data;
        
        console.log('[MensagemEtapaForm] Dados da mensagem:', mensagemData);
        
        const funilId = mensagemData.funil_id || '';
        const etapaId = mensagemData.etapa_id || '';
        
        console.log('[MensagemEtapaForm] funilId:', funilId, 'etapaId:', etapaId);
        
        // Garantir que o canal corresponde a uma origem válida
        let canalSelecionado = mensagemData.canal || '';
        if (origensData && origensData.length > 0) {
          const origemEncontrada = origensData.find(o => 
            o.nome?.toLowerCase() === canalSelecionado?.toLowerCase()
          );
          if (origemEncontrada) {
            canalSelecionado = origemEncontrada.nome;
          } else if (origensData.length > 0) {
            // Se não encontrou, usar a primeira origem
            canalSelecionado = origensData[0].nome;
          }
        }
        
        // Carregar etapas do funil primeiro, se houver funil
        let etapasList = [];
        let etapaIdFinal = etapaId;
        
        if (funilId) {
          try {
            const etapasResponse = await etapasAPI.listarPorFunil(funilId);
            if (etapasResponse.data.success) {
              etapasList = etapasResponse.data.data || etapasResponse.data || [];
              // Verificar se a etapa_id existe na lista
              if (etapaId) {
                const etapaExiste = etapasList.find(e => e.id === etapaId);
                if (!etapaExiste && etapasList.length > 0) {
                  // Se a etapa não existe mais, usar a primeira
                  etapaIdFinal = etapasList[0].id;
                }
              } else if (etapasList.length > 0) {
                etapaIdFinal = etapasList[0].id;
              }
            }
          } catch (err) {
            console.error('Erro ao carregar etapas:', err);
          }
          setEtapas(etapasList);
        }
        
        // Agora definir todos os dados de uma vez
        const novoFormData = {
          funil_id: funilId,
          etapa_id: etapaIdFinal,
          canal: canalSelecionado,
          mensagem: mensagemData.mensagem || '',
          ativa: mensagemData.ativa !== undefined ? mensagemData.ativa : true
        };
        
        console.log('[MensagemEtapaForm] Definindo formData:', novoFormData);
        console.log('[MensagemEtapaForm] Etapas disponíveis:', etapasList);
        console.log('[MensagemEtapaForm] Funis disponíveis:', funisData);
        
        setFormData(novoFormData);
      } else {
        setError('Mensagem não encontrada');
      }
    } catch (err) {
      console.error('Erro ao carregar mensagem:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    // Evitar chamadas duplicadas (React StrictMode pode causar isso)
    if (hasLoadedRef.current) {
      return;
    }
    
    if (isEditing && id) {
      hasLoadedRef.current = true;
      // Em modo de edição, carregar tudo dentro de carregarMensagem
      carregarMensagem();
    } else if (!isEditing) {
      hasLoadedRef.current = true;
      // Em modo de criação, carregar funis e origens separadamente
      carregarFunis();
      carregarOrigens();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  useEffect(() => {
    if (!isEditing && funis.length > 0 && !formData.funil_id) {
      const primeiroFunil = funis[0];
      handleInputChange('funil_id', primeiroFunil.id);
      carregarEtapasDoFunil(primeiroFunil.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funis]);

  useEffect(() => {
    if (!isEditing && origens.length > 0 && !formData.canal) {
      handleInputChange('canal', origens[0].nome || 'whatsapp');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origens]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      if (!formData.funil_id) {
        throw new Error('Selecione um funil');
      }
      if (!formData.etapa_id) {
        throw new Error('Selecione uma etapa');
      }
      if (!formData.canal.trim()) {
        throw new Error('Informe o canal');
      }
      if (!formData.mensagem.trim()) {
        throw new Error('Mensagem é obrigatória');
      }

      const payload = {
        funil_id: formData.funil_id,
        etapa_id: formData.etapa_id,
        canal: formData.canal.trim(),
        mensagem: formData.mensagem.trim(),
        ativa: Boolean(formData.ativa)
      };

      if (import.meta.env.DEV) {
        console.debug('[MensagemEtapaForm] Enviando payload', payload);
      }

      let response;
      if (isEditing) {
        response = await mensagensEtapaAPI.atualizar(id, payload);
      } else {
        response = await mensagensEtapaAPI.criar(payload);
      }

      if (response.data.success) {
        setSuccess(isEditing ? 'Mensagem atualizada com sucesso!' : 'Mensagem criada com sucesso!');
        setTimeout(() => navigate('/leads/mensagens-etapa'), 1200);
      } else {
        setError(response.data.error || 'Erro ao salvar mensagem');
      }
    } catch (err) {
      console.error('Erro ao salvar mensagem:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Erro ao conectar com o servidor. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate('/leads/mensagens-etapa');
  };

  if (loadingData) {
    return (
      <Layout title="Carregando...">
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
            <FiLoader style={{ fontSize: '2rem', animation: 'spin 1s linear infinite' }} />
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={isEditing ? 'Editar Mensagem' : 'Nova Mensagem'}>
      <Container>
        <Header>
          <BackButton onClick={handleVoltar}>
            <FiArrowLeft />
            Voltar
          </BackButton>
          <Title>
            {isEditing ? 'Editar Mensagem de Etapa' : 'Nova Mensagem de Etapa'}
          </Title>
        </Header>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <FormSection>
            <SectionTitle>
              <FiTarget />
              Contexto da Etapa
            </SectionTitle>

            <FormGrid>
              <FormGroup>
                <Label>
                  Funil <Required>*</Required>
                </Label>
                <Select
                  value={formData.funil_id}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange('funil_id', value);
                    handleInputChange('etapa_id', '');
                    carregarEtapasDoFunil(value);
                  }}
                  disabled={loading}
                >
                  <option value="">Selecione um funil</option>
                  {funis.map((funil) => (
                    <option key={funil.id} value={funil.id}>
                      {funil.nome}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>
                  Etapa <Required>*</Required>
                </Label>
                <Select
                  value={formData.etapa_id}
                  onChange={(e) => handleInputChange('etapa_id', e.target.value)}
                  disabled={!formData.funil_id || loadingEtapas}
                >
                  <option value="">
                    {formData.funil_id ? (loadingEtapas ? 'Carregando etapas...' : 'Selecione a etapa') : 'Selecione um funil'}
                  </option>
                  {etapas.map((etapa) => (
                    <option key={etapa.id} value={etapa.id}>
                      {etapa.ordem ? `#${etapa.ordem} - ` : ''}{etapa.nome}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiSend />
              Conteúdo
            </SectionTitle>

            <FormGrid>
              <FormGroup>
                <Label>
                  Canal <Required>*</Required>
                </Label>
              <Select
                value={formData.canal}
                onChange={(e) => handleInputChange('canal', e.target.value)}
                disabled={loading || origens.length === 0}
              >
                <option value="">
                  {origens.length === 0 ? 'Carregando origens...' : 'Selecione a origem/canal'}
                </option>
                {origens.map((origem) => (
                  <option key={origem.id} value={origem.nome}>
                    {origem.nome}
                  </option>
                ))}
              </Select>
              </FormGroup>

              <FormGroup>
                <CheckboxGroup>
                  <Checkbox
                    id="ativa"
                    type="checkbox"
                    checked={formData.ativa}
                    onChange={(e) => handleInputChange('ativa', e.target.checked)}
                  />
                  <CheckboxLabel htmlFor="ativa">
                    Mensagem ativa
                  </CheckboxLabel>
                </CheckboxGroup>
              </FormGroup>
            </FormGrid>

            <FormGroup>
              <Label>
                Mensagem <Required>*</Required>
              </Label>
              <Textarea
                placeholder="Digite o template da mensagem..."
                value={formData.mensagem}
                onChange={(e) => handleInputChange('mensagem', e.target.value)}
              />
            </FormGroup>
          </FormSection>

          <Actions>
            <Button type="button" onClick={handleVoltar}>
              Cancelar
            </Button>
            <Button $primary type="submit" disabled={loading}>
              {loading ? <FiLoader /> : <FiSave />}
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </Actions>
        </Form>
      </Container>
    </Layout>
  );
};

export default MensagemEtapaForm;

